import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CampaignIcon from "@mui/icons-material/Campaign";
import DescriptionIcon from "@mui/icons-material/Description";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HistoryIcon from "@mui/icons-material/History";
import InventoryIcon from "@mui/icons-material/Inventory";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import useAuthStore from "../../store/authStore";
import { getAdminRoleLabel, isPartnerRole } from "../../utils/adminAccess";

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED_WIDTH = 72;

const menuSections = [
  {
    title: "이코픽",
    scope: "ecopick",
    items: [
      { path: "/admin", label: "대시보드", icon: DashboardIcon, exact: true },
      { path: "/admin/users", label: "회원 관리", icon: PeopleIcon },
      { path: "/admin/locations", label: "수거 장소 관리", icon: LocationOnIcon },
      { path: "/admin/collections", label: "수거 요청 관리", icon: LocalShippingIcon },
      { path: "/admin/reports", label: "보고서 관리", icon: DescriptionIcon },
    ],
  },
  {
    title: "운영 현황",
    scope: "dream",
    items: [
      { path: "/admin/dream/operations", label: "운영 홈", icon: StorefrontIcon },
      { path: "/admin/dream/requests", label: "요청 운영", icon: AssignmentIcon },
    ],
  },
  {
    title: "회원·작업자",
    scope: "dream",
    items: [
      { path: "/admin/dream-users", label: "이용자 관리", icon: PeopleIcon },
      { path: "/admin/dream/collectors", label: "작업자 승인/관리", icon: PeopleIcon },
      { path: "/admin/dream/collector-work", label: "작업자 작업 현황", icon: BarChartIcon },
    ],
  },
  {
    title: "서비스 설정",
    scope: "dream",
    items: [
      { path: "/admin/dream/items", label: "품목/가격 관리", icon: InventoryIcon },
      { path: "/admin/dream/schedule", label: "예약/시간대 설정", icon: CalendarMonthIcon },
    ],
  },
  {
    title: "검수·리포트",
    scope: "dream",
    items: [
      { path: "/admin/dream/inspection", label: "완료 검수/수출 품목", icon: FactCheckIcon },
      { path: "/admin/dream/reports", label: "통계/리포트", icon: DescriptionIcon },
      { path: "/admin/dream/action-logs", label: "처리 이력", icon: HistoryIcon },
      { path: "/admin/customer-voice", label: "고객 목소리함", icon: CampaignIcon },
    ],
  },
];

function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  const visibleMenuSections = isPartnerRole(user)
    ? menuSections.filter((section) => section.scope === "dream")
    : menuSections;
  const headerTitle = isPartnerRole(user) ? "DREAM Console" : "ERUM Admin";

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: "100vh",
        backgroundColor: "#1a1f36",
        color: "white",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.2s ease, min-width 0.2s ease",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          px: collapsed ? 1 : 2.5,
          py: 2.5,
          minHeight: 72,
        }}
      >
        {!collapsed && (
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, letterSpacing: "-0.5px", color: "#2a9ac6" }}
          >
            {headerTitle}
          </Typography>
        )}
        <IconButton
          onClick={() => setCollapsed(!collapsed)}
          sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "white" } }}
        >
          {collapsed ? <MenuIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1, py: 1.5, overflowY: "auto" }}>
        {visibleMenuSections.map((section, sectionIndex) => (
          <Box key={section.title} sx={{ mb: sectionIndex === visibleMenuSections.length - 1 ? 0 : 1.5 }}>
            {!collapsed && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  px: 1.5,
                  py: 0.75,
                  color: "rgba(255,255,255,0.38)",
                  fontWeight: 700,
                }}
              >
                {section.title}
              </Typography>
            )}
            {collapsed && sectionIndex > 0 && (
              <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 1 }} />
            )}
            {section.items.map((item) => {
              const active = isActive(item);
              return (
                <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                  <Tooltip title={collapsed ? `${section.title} · ${item.label}` : ""} placement="right">
                    <ListItemButton
                      component={Link}
                      to={item.path}
                      sx={{
                        borderRadius: 2,
                        py: 1.2,
                        px: collapsed ? 2 : 2,
                        justifyContent: collapsed ? "center" : "flex-start",
                        backgroundColor: active
                          ? "rgba(42,154,198,0.15)"
                          : "transparent",
                        color: active ? "#2a9ac6" : "rgba(255,255,255,0.65)",
                        "&:hover": {
                          backgroundColor: active
                            ? "rgba(42,154,198,0.2)"
                            : "rgba(255,255,255,0.06)",
                          color: "white",
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 40,
                          color: "inherit",
                        }}
                      >
                        <item.icon fontSize="small" />
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: "0.9rem",
                            fontWeight: active ? 600 : 400,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </Box>
        ))}
      </List>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

      {/* User info & Logout */}
      <Box sx={{ px: collapsed ? 1 : 2, py: 2 }}>
        {!collapsed && user && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1.5,
              px: 1,
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#2a9ac6",
                fontSize: "0.85rem",
                mr: 1.5,
              }}
            >
              {user.name?.[0] || "A"}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {user.name || "관리자"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.5)" }}
              >
                {getAdminRoleLabel(user)}
              </Typography>
            </Box>
          </Box>
        )}
        <Tooltip title={collapsed ? "로그아웃" : ""} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              justifyContent: collapsed ? "center" : "flex-start",
              color: "rgba(255,255,255,0.5)",
              "&:hover": {
                backgroundColor: "rgba(231,76,60,0.15)",
                color: "#e74c3c",
              },
            }}
          >
            <ListItemIcon
              sx={{ minWidth: collapsed ? 0 : 40, color: "inherit" }}
            >
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="로그아웃"
                primaryTypographyProps={{ fontSize: "0.9rem" }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default Sidebar;
